<template>
  <v-container>
    <ApolloQuery
      :ref="queryRef"
      :query="(gql) => ordersQuery"
      :variables="{
        eventId: eventId,
        customerId: userId,
        confirmed: confirmed,
        resolution: resolution,
        fulfillmentStatus: fulfillmentStatus,
        startDate: filterDates.startDate,
        endDate: filterDates.endDate,
        orderBy: orderBy,
        itemsPerPage: itemsPerPage,
        nextCursor: nextCursor,
        prevCursor: prevCursor,
      }"
      :update="(data) => handleUpdate(data)"
    >
      <template #default="{ result: { error, data: orders }, isLoading }">
        <apollo-error-alert v-if="error" />
        <ekitabu-data-table
          v-model="selectedRows"
          :items="orders || []"
          item-key="id"
          :headers="customHeaders"
          class="data-table-container mb-6"
          disable-filtering
          :loading="!!isLoading"
          :mobile-breakpoint="$vuetify.breakpoint.thresholds.sm"
          :server-items-length="totalCount"
          :page="currentPage"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{
            'items-per-page-options': defaultPaginationOptions,
            'items-per-page-text': $t('transactionReports.itemsPerPage'),
          }"
          :loading-text="$t('transactionReports.loading')"
          :no-data-text="$t('transactionReports.noData')"
          @update:sort-by="updateSort('by', $event)"
          @update:sort-desc="updateSort('desc', $event)"
          @update:page="setCursor(pageData, $event)"
        >
          <template #item.id="{ item }">
            <router-link
              :to="{ name: 'orderOverview', params: orderRouteParams(item) }"
            >
              <span>{{ item._id }}</span>
            </router-link>
          </template>
          <template #item.orderNumber="{ item }">
            <span>{{ item.orderNumber ?? $t("common.none") }}</span>
          </template>
          <template #item.orderFulfillment.phoneNumber="{ item }">
            <span>{{
              item?.orderFulfillment?.phoneNumber || $t("common.none")
            }}</span>
          </template>
          <template #item.orderFulfillment.altPhoneNumber="{ item }">
            <span>{{
              item?.orderFulfillment?.altPhoneNumber ?? $t("common.none")
            }}</span>
          </template>
          <template #item.customerEmail="{ item }">
            <router-link
              :to="{ name: 'customer', params: customerRouteParams(item) }"
            >
              <span>{{ item.customerEmail ?? item.customerId ?? null }}</span>
            </router-link>
          </template>
          <template #item.updatedAt="{ item }">
            <span>{{ formatDate(item.updatedAt) }}</span>
          </template>
          <template #item.quantity="{ item }">
            <router-link
              :to="{ name: 'orderOverview', params: orderRouteParams(item) }"
            >
              <span>{{ getQuantity(item.orderLines) }}</span>
            </router-link>
          </template>
          <template #item.totalPriceSet="{ item }">
            {{ item.currency._id + " " + item.totalPriceSet }}
          </template>
          <template #item.resolution="{ item }">
            <v-chip
              small
              :color="item.resolution === 'Open' ? 'warning' : 'success'"
              text-color="textOnPrimary"
            >
              {{ $t("transactionReports.resolution." + item.resolution.toLowerCase()) }}
            </v-chip>
          </template>
          <template #item.fulfillmentStatus="{ item }">
            <span v-if="item.fulfillmentStatus">
              {{ $t("transactionReports.statuses." + item.fulfillmentStatus) }}
            </span>
            <span v-else>{{ $t("common.none") }}</span>
          </template>
          <template #item.provider="{ item }">
            <span>
              {{
                $t(
                  "paymentMethods." +
                    (getProviderDetails(item).provider ?? "none")
                )
              }}
            </span>
          </template>
        </ekitabu-data-table>
        <v-row>
          <v-spacer />
          <v-col class="col-12 col-sm-auto">
            <v-btn
              tile
              color="secondary"
              depressed
              :loading="!!isLoading"
              :disabled="!!isLoading"
              @click="refetchQuery(queryRef)"
            >
              {{ $t("transactionReports.refreshTransactions") }}
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </ApolloQuery>
  </v-container>
</template>

<script>
import ApolloErrorAlert from "@/components/ApolloErrorAlert.vue";
import { getProviderDetails } from "@/utils/PaymentProviderHelper";
import gql from "graphql-tag";
import OrderFragments from "@/graphql/OrderFragments";
import ServerPaginatedTableMixin from "@/mixins/ServerPaginatedTableMixin";
import { FROM_ROUTES } from "@/utils/Const";
import i18n from "@/i18n";

const ORDER_ADMIN_DETAILS_FRAGMENT = OrderFragments.orderAdminDetailsFragment;

const DEFAULT_HEADERS = [
  {
    text: i18n.t("transactionReports.orderId"),
    value: "id",
    class: "column-header",
    sortable: false,
  },
  {
    text: i18n.t("transactionReports.date"),
    value: "updatedAt",
    class: "column-header",
  },
  {
    text: i18n.t("transactionReports.quantity"),
    value: "quantity",
    class: "column-header",
    sortable: false,
  },
  {
    text: i18n.t("transactionReports.totalPrice"),
    value: "totalPriceSet",
    class: "column-header",
  },
  {
    text: i18n.t("transactionReports.paymentMethod"),
    value: "provider",
    class: "column-header",
    sortable: false,
  },
  { value: "data-table-expand" },
];

export default {
  name: "OrdersTable",
  components: {
    ApolloErrorAlert,
  },
  mixins: [ServerPaginatedTableMixin],
  props: {
    userId: {
      type: String,
      default: null,
    },
    eventId: {
      type: String,
      default: null,
    },
    fromPage: {
      type: String,
      default: null,
    },
    confirmed: {
      type: Boolean,
      default: true,
    },
    resolution: {
      type: String,
      default: null,
    },
    fulfillmentStatus: {
      type: String,
      default: null,
    },
    customHeaders: {
      type: Array,
      default: () => DEFAULT_HEADERS
    }
  },
  data() {
    return {
      queryRef: "customerOrdersReport",
      orderBy: {
        updatedAt: "DESC",
      },
      allItems: [],
    };
  },
  computed: {
    // Enables pagination persistence across navigation (mixin). Filter-aware so
    // a different filter/context starts fresh instead of restoring a stale page.
    paginationContextKey() {
      return [
        this.queryRef,
        this.fromPage ?? "",
        this.userId ?? "",
        this.eventId ?? "",
        this.confirmed,
        this.resolution ?? "",
        this.fulfillmentStatus ?? "",
        this.startDate ?? "",
        this.endDate ?? "",
        JSON.stringify(this.orderBy),
      ].join("|");
    },
    ordersQuery() {
      return gql`
        query getCustomerOrders(
          $eventId: String
          $customerId: String
          $confirmed: Boolean
          $resolution: String
          $fulfillmentStatus: String
          $startDate: String
          $endDate: String
          $orderBy: OrderFilter_order
          $itemsPerPage: Int
          $nextCursor: String
          $prevCursor: String
        ) {
          orders(
            event_id: $eventId
            customerId: $customerId
            confirmed: $confirmed
            resolution: $resolution
            fulfillmentStatus: $fulfillmentStatus
            updatedAt: { after: $startDate, before: $endDate }
            order: [$orderBy]
            first: $itemsPerPage
            after: $nextCursor
            before: $prevCursor
          ) {
            totalCount
            pageInfo {
              endCursor
              startCursor
              hasNextPage
              hasPreviousPage
            }
            edges {
              cursor
              node {
                ...OrderAdminDetails
              }
            }
          }
        }
        ${ORDER_ADMIN_DETAILS_FRAGMENT}
      `;
    },
  },
  methods: {
    handleUpdate(data) {
      const result = this.updateData(data, 'orders');
      this.allItems = result;
      this.$emit('data-loaded', result);
      return result;
    },
    orderRouteParams(item) {
      const params = {};
      params.orderId = item._id;
      params.customerId = item.customerId;
      if (this.fromPage === FROM_ROUTES.customer) {
        params.customerName = this.$route.params.customerName
      }
      else if (this.fromPage === FROM_ROUTES.event) {
        params.eventId = this.$route.params.id;
        params.eventName = this.$route.params.eventName;
      }
      else if (this.fromPage === FROM_ROUTES.orderReport) {
        params.fromOrderReport = true;
      }
      return params;
    },
    customerRouteParams(item) {
      const params = {};
      params.id = item?.customerId
      if (this.fromPage === FROM_ROUTES.orderReport) {
        params.fromOrderReport = true;
        params.customerName = item?.customerEmail
      }
      return params
    },
    getProviderDetails(item) {
      return getProviderDetails(item);
    },
    getQuantity(orderLines) {
      let quantity = 0;
      if (orderLines.edges?.length > 0) {
        quantity = orderLines.edges.reduce((acc, edge) => {
          return acc + edge.node.quantity;
        }, 0);
      }
      return quantity;
    },
  },
};
</script>
